import { Box, Link, Typography } from '@mui/material'
import { InvoiceCloudFrame, MButton, ProgressLoaders, Text } from 'components'
import { ROUTES_PATH, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatValue, useLocalStorage } from 'utils'
import {
  type BillingData,
  type IPast12MonthsUsageData
} from 'utils/hooks/types'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { DashboardContainer } from './DashboardContainer'
import UtilityItem, { type UtilityItemType } from './UtilityItem'

interface IBill {
  past12MonthsUsageData: IPast12MonthsUsageData[]
  isLoading: boolean
  latestBill?: BillingData[] | null
}

export const Bill = ({
  past12MonthsUsageData,
  isLoading,
  latestBill
}: IBill): JSX.Element => {
  const {
    account,
    currentAccount,
    billing,
    getAccount,
    isAdmin,
    userEmail,
    services
  } = useAuth()
  const isMobile = useMobileBreakpoints()
  const navigate = useNavigate()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [prevAccountId, setPrevAccountId] = useState<string>('')
  const [utilityItems, setUtilityItems] = useState<UtilityItemType[]>([])
  const [communityChargeTotal, setCommunityChargeTotal] = useState<string | null>(null)
  const [adjustmentAndFees, setAdjustmentAndFees] = useState<string | null>(null)

  useEffect(() => {
    if (!services || !latestBill) return
    const updatedItems = services.map(service => {
      const match = latestBill.find((bill: BillingData) =>
        bill.service?.toLowerCase().includes(service.toLowerCase())
      )
      const rounded =
        match?.billAmount !== ''
          ? (Math.round(parseFloat(String(match?.billAmount)) * 100) / 100).toFixed(2)
          : ''

      return {
        type: service.toLowerCase(),
        cost: rounded
      }
    })
    setUtilityItems(updatedItems)

    const commCharge = latestBill.find((bill: BillingData) =>
      bill.service?.toLowerCase() === 'community charges'
    )
    const commChargeRounded =
      commCharge && commCharge.billAmount !== ''
        ? (Math.round(parseFloat(String(commCharge.billAmount)) * 100) / 100).toFixed(2)
        : null
    setCommunityChargeTotal(commChargeRounded)

    const adjustments = latestBill.find((bill: BillingData) =>
      bill.service?.toLowerCase() === 'adjustments & fees'
    )
    const adjustmentsRounded =
      adjustments && adjustments.billAmount !== ''
        ? (Math.round(parseFloat(String(adjustments.billAmount)) * 100) / 100).toFixed(2)
        : null
    setAdjustmentAndFees(adjustmentsRounded)
  }, [services, billing, latestBill])

  useEffect(() => {
    if (
      prevAccountId === currentAccount?.accountId ||
      currentAccount?.accountId === account?.myAccount?.accountId
    ) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
    setPrevAccountId(
      currentAccount?.accountId ?? account?.myAccount?.accountId ?? ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentAccount])

  if (isLoading) {
    return (
      <DashboardContainer
        minHeight={past12MonthsUsageData.length === 1 ? '26vh' : '35vh'}
      >
        <ProgressLoaders height='100%' />
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer
      minHeight={past12MonthsUsageData.length === 1 ? '26vh' : '35vh'}
    >
      <Box display='flex' flexDirection='column' gap={2}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' flexDirection='column' gap={1}>
            <Text color={color.black} fontSize='18px' fontWeight='700'>
              {services.length === 1
                ? services[0]?.charAt(0) +
                services[0]?.substring(1).toLowerCase()
                : 'Usage'}{' '}
              Bill
            </Text>
            <Text color={color.grey[700]} fontSize='12px'>
              Account Number: {account?.myAccount.accountId}
            </Text>
            <Box>
              <Typography display="inline" color={color.grey[700]} fontSize={12}>
                Residential Service Bill Period:
              </Typography>
              <Typography display="inline" fontSize={12} color={color.grey[900]} fontWeight={600} ml={0.5}>{`${dayjs(account?.myAccount?.billStartDt).format('M/DD/YYYY')} to ${dayjs(account?.myAccount?.billEndDt).format('M/DD/YYYY')}`}</Typography>
            </Box>
          </Box>
          {!isMobile && (
            <InvoiceCloudFrame page={1}>
              <MButton
                variant='contained'
                size='large'
                rounded='true'
                texttransform='none'
              >
                Pay Bill
              </MButton>
            </InvoiceCloudFrame>
          )}
        </Box>

        {utilityItems.length > 1 && (
          <Box
            display='flex'
            gap={2}
            flexDirection={isMobile ? 'column' : 'row'}
            borderBottom={`solid 1px ${color.background[300]}`}
            pb={3}
          >
            {utilityItems.map((item, index) => (
              <UtilityItem key={index} type={item?.type} cost={item?.cost} />
            ))}
          </Box>
        )}
      </Box>

      <Box display='flex' py={2} flexDirection={isMobile ? 'column' : 'row'}>
        <Box display='flex' flexDirection='column' flex={1} gap={1}>
          <Link
            fontSize={14}
            onClick={() => {
              navigate(ROUTES_PATH.BILLING)
            }}
          >
            Details
          </Link>
          {adjustmentAndFees && (
            <Box
              display='flex'
              flexDirection={isMobile ? 'row' : 'column'}
              justifyContent='space-between'
              alignItems={isMobile ? 'center' : 'left'}
            >
              <Typography color={color.grey[700]} fontSize={12}>
                Adjustment & Fees:
              </Typography>
              <Typography fontWeight={600} fontSize={18}>
                {formatValue(adjustmentAndFees)}
              </Typography>
            </Box>
          )}
          <Box
            display='flex'
            flexDirection={isMobile ? 'row' : 'column'}
            justifyContent='space-between'
            alignItems={isMobile ? 'center' : 'left'}
          >
            <Typography color={color.grey[700]} fontSize={12}>
              Previous Balance:
            </Typography>
            <Typography color={color.grey[900]} fontWeight={600} fontSize={18}>
              {account?.myAccount?.previousBalance != null &&
                formatValue(account?.myAccount?.previousBalance)}
            </Typography>
          </Box>
          {communityChargeTotal && (
            <Box
              display='flex'
              flexDirection={isMobile ? 'row' : 'column'}
              justifyContent='space-between'
              alignItems={isMobile ? 'center' : 'left'}
            >
              <Typography color={color.grey[700]} fontSize={12}>
                Community Charges
              </Typography>
              <Typography fontWeight={600} fontSize={18}>
                {formatValue(communityChargeTotal)}
              </Typography>
            </Box>
          )}
          <Box
            display='flex'
            flexDirection={isMobile ? 'row' : 'column'}
            justifyContent='space-between'
            alignItems={isMobile ? 'center' : 'left'}
          >
            <Typography color={color.grey[700]} fontSize={12}>
              Late Payment/Penalty Charges:
            </Typography>
            <Typography color='error' fontWeight={600} fontSize={18}>
              {account?.myAccount?.lateFees != null &&
                formatValue(account?.myAccount?.lateFees)}
            </Typography>
          </Box>
        </Box>
        <Box
          display='flex'
          flexDirection='column'
          flex={1}
          gap={0.5}
          my={isMobile ? 2 : 0}
        >
          <Box
            display='flex'
            flexDirection={isMobile ? 'row' : 'column'}
            justifyContent='space-between'
            pt={isMobile ? 2 : 0}
            borderTop={isMobile ? `solid 1px ${color.background[400]}` : 'none'}
          >
            <Typography color={color.grey[900]} fontSize={12} letterSpacing={1}>
              BALANCE DUE
            </Typography>
            <Typography color={color.grey[700]} fontSize={10}>
              Due Date:{' '}
              {account?.myAccount?.dueDate != null &&
                dayjs(account?.myAccount?.dueDate).format('MMM D, YYYY')}
            </Typography>
          </Box>
          <Typography
            fontWeight={700}
            fontSize={isMobile ? '30px' : '52px'}
            bgcolor={color.background[50]}
            py={1}
            borderRadius={1}
            border={`solid 1px ${color.background[400]}`}
            width='100%'
            textAlign='center'
          >
            {account?.myAccount?.accountBalance != null &&
              formatValue(account?.myAccount?.accountBalance)}
          </Typography>
        </Box>
        {isMobile && (
          <InvoiceCloudFrame page={1}>
            <MButton
              variant='contained'
              size='large'
              rounded='true'
              texttransform='none'
              fullWidth
            >
              Pay Bill
            </MButton>
          </InvoiceCloudFrame>
        )}
      </Box>
    </DashboardContainer>
  )
}
